import { Button, ButtonTheme, NewWindowIcon } from '@vivino/js-react-common-ui';
import { CountryFlagIcon48, isEmpty, isVCMerchant, trackEvent } from '@vivino/js-web-common';
import { RubyLibCheckoutPrice, RubyLibMerchant } from '@webtypes/rubyLibApi';
import React from 'react';
import { isStateBasedShipping } from 'vivino-js/helpers/countryHelpers';
import t from 'vivino-js/translationString';
import Price from 'vivino-ui/atoms/Price';

import { isLinkoutMerchant } from '@lib/merchants';

import AddToCart from 'vivino-ui/components/AddToCart';

import fallbackMerchantImage from './merchant_default_icon@2x.png';
import styles from './shop.scss';

const TRANSLATIONS = {
  basedInLocation: 'components.shared.modals.shops_modal.based_in_location',
  buyFor: 'components.shared.modals.shops_modal.buy_for',
};

interface ShopPresentationalProps {
  vintageId: number;
  merchant: RubyLibMerchant;
  price: RubyLibCheckoutPrice;
  onClickPPC: () => void;
  wineryId: number;
  sponsored: boolean;
}

const ShopPresentational = ({
  merchant,
  price,
  vintageId,
  onClickPPC,
  wineryId,
  sponsored,
}: ShopPresentationalProps) => {
  if (isEmpty(price) || isEmpty(price.url)) {
    return null;
  }

  const onLinkoutButtonClick = () => {
    trackEvent({
      event: 'Web Wine Page - Linkout - Click',
      props: {
        linkoutUrl: price?.url,
        priceTypeAmount: 'linkout',
        vintageId,
        priceAmount: price?.amount,
        currency: price?.currency?.code,
        wineryId,
        sponsored,
      },
    });
  };

  const renderMerchantLocation = () => {
    const countryCode = merchant?.country?.code;
    const countryName = merchant?.country?.name;
    const stateCode = merchant?.state;

    const hasStateCode = !!stateCode;

    if (!countryCode || !countryName) {
      return null;
    }

    const result = [
      <CountryFlagIcon48
        key={countryCode}
        countryCode={countryCode}
        countryName={countryName}
        className={styles.flag}
      />,
    ];

    if (isStateBasedShipping(countryCode) && hasStateCode) {
      return [
        ...result,
        t(TRANSLATIONS.basedInLocation, {
          location: `${merchant.state}, ${countryName}`,
        }),
      ];
    }

    return [
      ...result,
      t(TRANSLATIONS.basedInLocation, {
        location: countryName,
      }),
    ];
  };

  const renderBuyableMerchant = (merchant) => {
    if (isVCMerchant(merchant)) {
      return <AddToCart price={price} seenVintageId={vintageId} fullWidth />;
    } else if (isLinkoutMerchant(merchant)) {
      return (
        <Button
          target="_blank"
          fullWidth
          theme={ButtonTheme.Accent}
          startIcon={<NewWindowIcon />}
          href={price?.url}
          onClick={onLinkoutButtonClick}
        >
          {t(TRANSLATIONS.buyFor, {
            price: (
              <Price
                amount={price.amount}
                currency={price.currency}
                className={styles.priceButton}
              />
            ),
          })}
        </Button>
      );
    } else {
      return null;
    }
  };

  const merchantDescription = (merchant) => {
    if (isLinkoutMerchant(merchant)) {
      return merchant?.selling_points?.[0].description;
    } else {
      return merchant?.description;
    }
  };

  const merchantImage = merchant?.image?.location || fallbackMerchantImage;

  return (
    <div className={styles.shop} data-testid="merchantShop">
      <div className={styles.icon} style={{ backgroundImage: `url(${merchantImage})` }} />
      <div className={styles.middleSection}>
        <div className={styles.name}>{merchant.name}</div>
        <div className={styles.description}>{merchantDescription(merchant)}</div>
        <div className={styles.country} data-testid="merchantLocation">
          {renderMerchantLocation()}
        </div>
      </div>
      <div className={styles.endSection}>
        {isVCMerchant(merchant) || isLinkoutMerchant(merchant) ? (
          renderBuyableMerchant(merchant)
        ) : (
          <>
            <Button fullWidth href={price.url} onClick={onClickPPC} dataTestId="ppcButton">
              <Price amount={price.amount} currency={price.currency} />
            </Button>
            {
              //TODO : Wrap PPC button into a reusable component if needed in other places
              price.message && <div className={styles.message}>{price.message}</div>
            }
          </>
        )}
      </div>
    </div>
  );
};

export default ShopPresentational;
